import {
  onUnmounted,
} from 'vue';
import { useStore } from 'vuex';
import { storeToRefs } from 'pinia';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { v4 as uuidv4 } from 'uuid';
import { useSnapshotStore } from '@/stores/useSnapshotStore.js';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import useTaskModal from '@/composables/useTaskModal.js';

export default function useSnapshot() {
  const {
    snapshotFiles,
  } = storeToRefs(useSnapshotStore());

  const {
    $reset,
  } = useSnapshotStore();

  const {
    pauseGetTasks,
    resumeGetTasks,
    isGettingTasks,
    getTasks,
  } = useTaskModal();

  const $toast = useToast();
  const store = useStore();

  onUnmounted(() => {
    $reset();
  });

  async function processFolder(directoryHandle) {
    const tree = { [directoryHandle.name]: {} };
    snapshotFiles.value = [];
    await handleDirectory(directoryHandle, tree, directoryHandle.name);
    try {
      const fileObjs = await Promise.all(snapshotFiles.value);
      await compileFiles(fileObjs);
    } catch (e) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: `Error processing files: ${e}`,
        type: "error",
        dismissible: true,
        position: "top",
      });
    }
  }

  async function processFolderAndReturnFiles(directoryHandle) {
    try {
      const tree = { [directoryHandle.name]: {} };
      snapshotFiles.value = [];
      await handleDirectory(directoryHandle, tree, directoryHandle.name);
      return await Promise.all(snapshotFiles.value);
    } catch (e) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: `Error processing files: ${e}`,
        type: "error",
        dismissible: true,
        position: "top",
      });
    }
  }

  async function restoreSnapshot(params) {
    const dataConnect = new DatastoreConnect();
    return dataConnect.restoreSnapshot(params);
  }

  async function compileFiles(params, fileList) {
    snapshotFiles.value = fileList;
    let relativePath = "";
    if (snapshotFiles.value && snapshotFiles.value.length > 0) {
      relativePath = snapshotFiles.value[0].relativePath || snapshotFiles.value[0].webkitRelativePath;
    }

    let dirName = "";
    if (relativePath && relativePath !== "") {
      // From folder select
      dirName = relativePath.split("/")[0];
    } else {
      dirName = snapshotFiles.value[0].name;
      dirName = dirName.slice(0, dirName.lastIndexOf('.')) || dirName;
    }
    const result = await uploadFiles(params, snapshotFiles.value, dirName);
    params.snapshot_id = result.snapshot_id;
    params.task_id = result.task_id;
    return restoreSnapshot(params);
  }

  async function handleDirectory(dirhandle, tree, relativePath) {
    const subtree = {};
    tree[dirhandle.name] = subtree;
    for await (const [name, handle] of dirhandle) {
      if (handle.kind === "directory") {
        const newRelativePath = relativePath ? `${relativePath}/${name}` : name;
        await handleDirectory(handle, subtree, newRelativePath);
      } else {
        subtree[name] = "[file]";
        snapshotFiles.value.push(new Promise((resolve, reject) => {
          handle.getFile().then((file) => {
            file.relativePath = `${relativePath}/${name}`;
            resolve(file);
          });
        }));
      }
    }
  }

  async function uploadFiles(params, fileList, name) {
    let snapshot_id = 0;
    const fileNames = fileList.map((file) => file.name);
    const fileSizes = fileList.map((file) => file.size);
    const newFileList = [];
    const dataConnect = new DatastoreConnect();
    try {
      const response = await dataConnect.createSnapshotUploadMultipartUrl({
        keys: fileNames,
        snapshot_name: name,
        file_sizes: fileSizes,
      });
      if (response && response.result) {
        const firstKey = Object.keys(response.result)[0];
        const directoryPath = firstKey.substring(0, firstKey.lastIndexOf("/"));
        snapshot_id = response.result["snapshot_id"];
        fileList.forEach((file, i) => {
          newFileList.push({
            file,
            size: fileSizes[i],
            urls: response.result[`${directoryPath}/${file.name}`]["urls"],
            uploadId: response.result[`${directoryPath}/${file.name}`]["upload_id"],
            key: `${directoryPath}/${file.name}`,
          });
        });
      }
    } catch (err) {
      throw new Error(`Error creating uploading urls: ${err}`);
    }

    const totalSize = fileSizes.reduce((acc, size) => acc + size, 0); // Calculate total size of all files
    let currentTime = 0;
    await dataConnect.getCurrentTime()
      .then((resp) => {
        if (resp.result) {
          currentTime = resp.result;
        }
      });

    // const task = {
    //   name: `Upload ${name} to Cloud Storage`,
    //   docker_id: '',
    //   type: 'upload',
    //   status: 'running',
    //   count: 0,
    //   total: totalSize,
    //   id: uuidv4(),
    //   isLocal: true,
    //   date: currentTime,
    //   unit: 'bytes',
    // };

    // store.commit('tasks/addSnapshotUploadTask', task);

    const dbTask = await dataConnect.addDockerTask({
      name: `Import EdgeFirst Dataset to ${params.dataset_name}`,
      project_id: params.project_id,
      docker_id: '',
      type: 'upload',
      status: 'running',
      date: currentTime,
      data: {
        stage_definitions: [
          { upload_snapshot: "Uploading Files to Cloud Storage" },
        ],
      },
    }).then((resp) => {
      if (resp.result) {
        return resp.result;
      }
      throw Error("Failed to create import task in database");
    })
      .catch((error) => {
        $toast.open({
          queue: true,
          duration: 5000,
          message: `Error creating task in database: ${error}`,
          type: 'error',
          dismissible: true,
          position: 'top',
        });
      });

    await dataConnect.updateDataset({
      dataset_id: params.dataset_id,
      docker_task_id: dbTask.id,
    })
      .catch((error) => {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to update dataset: ${error}`,
          type: "error",
          position: "top",
        });
      });

    store.commit('datasets/setRefreshDatasetId', params.dataset_id);
    getTasks();
    if (!isGettingTasks.value) {
      resumeGetTasks();
    }

    const sizeMap = {};
    const fileMap = {};
    const totalParts = newFileList.reduce((total, file) => total + file["urls"].length, 0);
    let completedPart = 0;
    let current_percentage = 0;
    const uploadPromises = newFileList.map((fileObj) => uploadFileMultipart(
      fileObj.file,
      fileObj.urls,
      fileObj.uploadId,
      (fileName, fileEvent, totalFileParts, partIndex) => {
        if (!(fileName in sizeMap)) {
          sizeMap[fileName] = Array(totalFileParts).fill(0);
        }
        sizeMap[fileName][partIndex] += (fileEvent.loaded - sizeMap[fileName][partIndex]);
        const countSum = Object.values(sizeMap)
          .flat() // Flatten the arrays into a single array
          .filter((num) => !Number.isNaN(num)) // Filter out NaN values
          .reduce((sum, num) => sum + num, 0); // Sum up all the numbers

        if (Math.floor((countSum / totalSize) * 100) > current_percentage) {
          current_percentage = Math.floor((countSum / totalSize) * 100);
          dataConnect.updateTaskStage({
            docker_task_id: dbTask.id,
            stage: "upload_snapshot",
            label: "Uploading Files to Cloud Storage",
            message: "",
            status: "uploading",
            percentage: current_percentage,
          });
        }
      },
      async (PartNumber, ETag, fileName, uploadId, totalFileParts) => {
        completedPart += 1;
        if (!(fileName in fileMap)) {
          fileMap[fileName] = {
            uploadedData: [],
            totalFileParts,
            uploadId,
          };
        }
        fileMap[fileName]["uploadedData"].push({ PartNumber, ETag });
        if (fileMap[fileName]["uploadedData"].length === fileMap[fileName]["totalFileParts"]) {
          const completeResponse = await dataConnect.completeSnapshotUploadMultipartUrl({
            key: fileObj.key,
            upload_id: fileObj.uploadId,
            etag_list: fileMap[fileName]["uploadedData"].sort((a, b) => a.PartNumber - b.PartNumber),
          });
          if (completeResponse && completeResponse.error) {
            $toast.open({
              queue: true,
              duration: 5000,
              message: `Error uploading files: ${completeResponse.error.message}`,
              type: 'error',
              dismissible: true,
              position: 'top',
            });
          }
        }
        if (completedPart === totalParts) {
          // store.commit('tasks/updateSnapshotUploadTaskStatus', task);
          dataConnect.updateTaskStage({
            docker_task_id: dbTask.id,
            stage: "upload_snapshot",
            label: "Uploading Files to Cloud Storage",
            message: "",
            status: "running",
            percentage: 100,
          });
          updateStatus(snapshot_id, "available");
        }
      },
      (error) => {
        dataConnect.updateTaskStage({
          docker_task_id: dbTask.id,
          stage: "upload_snapshot",
          label: "Uploading Files to Cloud Storage",
          message: `Error: ${error}`,
          status: "error",
          percentage: current_percentage,
        });
        // store.commit('tasks/updateSnapshotUploadTaskStatus', task);
        $toast.open({
          queue: true,
          duration: 5000,
          message: `Error uploading files: ${error}`,
          type: 'error',
          position: 'top',
          dismissible: true,
        });
      },
    ));
    await Promise.all(uploadPromises);

    store.commit('datasets/setRefreshDatasetId', params.dataset_id);

    return { snapshot_id, task_id: dbTask.id };
  }

  function uploadFileMultipart(file, presignedUrls, uploadId, progressCallback, partCompleteCallback, errorCallback) {
    return new Promise((resolve, reject) => {
      const CHUNK_SIZE = 100 * 1024 * 1024; // 100MB per part
      const totalParts = presignedUrls.length;
      let completedParts = 0;

      for (let i = 0; i < totalParts; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        const presignedUrl = presignedUrls[i];

        const xhr = new XMLHttpRequest();
        xhr.open("PUT", presignedUrl, true);

        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const fileEvent = event;
            progressCallback(file.name, fileEvent, totalParts, i);
          }
        });

        xhr.onload = () => {
          if (xhr.status === 200) {
            const etag = xhr.getResponseHeader("ETag");
            partCompleteCallback(i + 1, etag.replace(/^"|"$/g, ''), file.name, uploadId, totalParts);
            completedParts += 1;
            if (completedParts === totalParts) {
              resolve(); // All parts uploaded successfully
            }
          } else {
            reject();
          }
        };

        xhr.onerror = (e) => {
          errorCallback(e);
          reject();
        };

        xhr.send(chunk);
      }
    });
  }

  async function updateStatus(snapshot_id, status) {
    const dataConnect = new DatastoreConnect();
    await dataConnect.updateSnapshot({
      snapshot_id,
      status,
    }).catch((e) => {
      $toast.open({
        queue: true,
        duration: 5000,
        message: e,
        type: 'error',
        dismissible: true,
        position: 'top',
      });
    });
  }

  async function handleFileInput(event) {
    const selectedFiles = Array.from(event.target.files);
    try {
      await compileFiles(selectedFiles);
    } catch (e) {
      $toast.open({
        queue: true,
        duration: 5000,
        message: `Error uploading files: ${e}`,
        type: "error",
        position: "top",
      });
    }
  }

  return {
    snapshotFiles,
    compileFiles,
    processFolder,
    processFolderAndReturnFiles,
    handleFileInput,
  };
}
